import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { mapSupabaseError, getUserFriendlyMessage } from '../lib/errors';
import { captureError } from '../lib/sentry';
import { useToast } from '../components/Toast';

export function usePayments(itemId) {
  const toast = useToast();
  
  return useQuery({
    queryKey: ['payments', itemId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('payments')
        .select('*')
        .eq('item_id', itemId)
        .order('payment_date', { ascending: false });

      if (error) {
        const apiError = mapSupabaseError(error);
        captureError(apiError, { 
          tags: { feature: 'payments', action: 'fetch' },
          extra: { itemId }
        });
        throw apiError;
      }

      return data;
    },
    enabled: !!itemId,
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 10,
    onError: (error) => {
      const apiError = mapSupabaseError(error);
      toast.error(getUserFriendlyMessage(apiError));
    },
  });
}

export function useAddPayment() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (paymentData) => {
      const { data, error } = await supabase
        .from('payments')
        .insert([paymentData])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onError: (error) => {
      const apiError = mapSupabaseError(error);
      captureError(apiError, {
        tags: { feature: 'payments', action: 'create' },
      });
      toast.error(getUserFriendlyMessage(apiError));
    },
    onSuccess: (data) => {
      toast.success('გადახდა დაემატა!');
      queryClient.invalidateQueries({ queryKey: ['payments', data.item_id] });
      queryClient.invalidateQueries({ queryKey: ['item', data.item_id] });
      queryClient.invalidateQueries({ queryKey: ['items'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
    },
  });
}

export function useDeletePayment() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async ({ id, itemId }) => {
      const { error } = await supabase
        .from('payments')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return { id, itemId };
    },
    onError: (error) => {
      const apiError = mapSupabaseError(error);
      captureError(apiError, {
        tags: { feature: 'payments', action: 'delete' },
      });
      toast.error(getUserFriendlyMessage(apiError));
    },
    onSuccess: (result) => {
      toast.success('გადახდა წაიშალა!');
      queryClient.invalidateQueries({ queryKey: ['payments', result.itemId] });
      queryClient.invalidateQueries({ queryKey: ['item', result.itemId] });
      queryClient.invalidateQueries({ queryKey: ['items'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
    },
  });
}

export function useItemPaymentSummary(itemId) {
  const toast = useToast();
  
  return useQuery({
    queryKey: ['payment-summary', itemId],
    queryFn: async () => {
      const [itemResult, paymentsResult] = await Promise.all([
        supabase
          .from('items')
          .select('total_cost_gel, total_paid_gel, payment_status, purchase_price_gel, weight_cost_gel, customs_cost_gel')
          .eq('id', itemId)
          .single(),
        supabase
          .from('payments')
          .select('*')
          .eq('item_id', itemId)
      ]);

      if (itemResult.error) {
        throw mapSupabaseError(itemResult.error);
      }

      const item = itemResult.data;
      const payments = paymentsResult.data || [];
      
      const totalPaid = parseFloat(item.total_paid_gel) || 0;
      const totalCost = parseFloat(item.total_cost_gel) || 0;
      const remaining = Math.max(0, totalCost - totalPaid);
      
      const itemCostPaid = payments
        .filter(p => p.payment_type === 'item_cost')
        .reduce((sum, p) => sum + parseFloat(p.amount), 0);
      
      const weightCostPaid = payments
        .filter(p => p.payment_type === 'weight_cost')
        .reduce((sum, p) => sum + parseFloat(p.amount), 0);
      
      const customsCostPaid = payments
        .filter(p => p.payment_type === 'customs_cost')
        .reduce((sum, p) => sum + parseFloat(p.amount), 0);

      return {
        totalCost,
        totalPaid,
        remaining,
        paymentStatus: item.payment_status || 'unpaid',
        itemCostPaid,
        weightCostPaid,
        customsCostPaid,
        purchasePriceGel: parseFloat(item.purchase_price_gel) || 0,
        weightCostGel: parseFloat(item.weight_cost_gel) || 0,
        customsCostGel: parseFloat(item.customs_cost_gel) || 0,
        paymentCount: payments.length
      };
    },
    enabled: !!itemId,
    staleTime: 1000 * 60,
    onError: (error) => {
      const apiError = mapSupabaseError(error);
      toast.error(getUserFriendlyMessage(apiError));
    },
  });
}
